import { useContext, useCallback } from "react";

import JSON5 from "json5";
import { default as PQueue } from "p-queue";

import { Api } from "interfaces";
import { ApiContext } from "api/api-context";

import { processError, NETWORK_NOT_AVAILABLE, SERVER_ERROR } from "./errors";
import buildRequest, { RequestType } from "./build-request";

const RequestsQueue = new PQueue({
  concurrency: 10,
  intervalCap: 10,
  interval: 1000,
});

const delay = (t: number) => new Promise((r) => setTimeout(r, t));

export interface RequestProps {
  url: string;
  method?: RequestType;
  data?: { [key: string]: any } | string | number;
  timeout?: number;
  files?: File | File[] | { [key: string]: File | File[] };
  onUploadProgress?: (progress: number) => unknown;
  onAuthFailure?: () => any;
  authToken?: string;
}

export async function makeFetchRequest<T = any>({
  url,
  method = "GET",
  data,
  timeout = 1000 * 60,
  files,
  onUploadProgress,
  onAuthFailure,
  authToken,
}: RequestProps): Promise<T> {
  const errorFunc = processError(onAuthFailure);

  let response: Api.FetcherResponse = await RequestsQueue.add(() =>
    buildRequest(url, method, data, timeout, files, onUploadProgress, authToken)
  )
    .then((resp) => resp)
    .catch(errorFunc);

  // if (response.status === 999) {
  //   throw NETWORK_NOT_AVAILABLE;
  // }

  if (response.status >= 500 && ![502, 503, 999].includes(response.status)) {
    throw SERVER_ERROR;
  }

  let retries = 0;
  const startTime = new Date().valueOf();

  // Exponential backoff.
  while (
    [429, 502, 503, 999].includes(response.status) &&
    (new Date().valueOf() - startTime) / 1000 <= timeout
  ) {
    const jitter = Math.floor(Math.random() * 1000);
    const timeToWait = 1000 * Math.pow(2, retries);
    await delay(timeToWait + jitter);
    retries += 1;

    response = await RequestsQueue.add(() =>
      buildRequest(url, method, data, timeout, files, onUploadProgress)
    )
      .then((resp) => resp)
      .catch(errorFunc);
  }

  // Final error, after all the time has passed.
  if (response.status < 200 || response.status >= 400) {
    throw response.error;
  }

  try {
    if (typeof response.data === "string") {
      return JSON5.parse(response.data);
    }
    return response.data;
  } catch {
    return response.data;
  }
}

export function useGetFetcher<T = any>() {
  const onAuthFailure = useContext(ApiContext);
  return useCallback(
    (props: RequestProps) => {
      if (props.onAuthFailure || !onAuthFailure) {
        return makeFetchRequest<T>(props);
      }

      return makeFetchRequest<T>({ ...props, onAuthFailure: onAuthFailure });
    },
    [onAuthFailure]
  );
}

export default makeFetchRequest;
